// ─── Nova AI Redux slice ──────────────────────────────────────────────────────
// Holds transient Nova AI navigation intents.
// WorkOrdersScreen reads repairNavigation and applies filters, then clears it.
// No other existing slices are touched.

import {createSlice, PayloadAction} from '@reduxjs/toolkit';

export interface NovaAIRepairNavigation {
  /** Status tab to select: "Open" | "All" | "Complete" */
  statusFilter?: string;
  /** Scope dropdown value:
   *  "0" = My Repairs / My Shop
   *  "1" = My Repairs / All Shops
   *  "2" = All Repairs / My Shop
   *  "3" = All Repairs / All Shops
   */
  scopeFilter?: string;
  /** Priority sort value matching sortOptions/priorityDropdownOptions .value:
   *  "RED" = Urgent, "UR" = High, "ASAP" = Medium, "WP" = Low, "" = All
   */
  sortFilter?: string;
  /** View mode to switch to: "List" | "Card" (Tile) */
  viewFilter?: string;
  /**
   * 1-based page number for the Repairs list (WorkOrdersScreen / DashboardScreen).
   * Set when Nova AI returns filters.page on the Repairs or Home path.
   */
  pageFilter?: number;
  /**
   * Dashboard section tab to activate when navigating to Home.
   * Matched case-insensitively against dashboardTabs[].value
   * (e.g. "parts inventory" → 'Parts Inventory', "repairs" → 'Repairs').
   */
  dashboardTabFilter?: string;
  /**
   * Parts Inventory sub-tab to activate when navigating to Parts.
   * Matched case-insensitively against partsTabs[].value
   * (e.g. "active requests" → 'Active Requests').
   */
  partsInventoryTabFilter?: string;
  /**
   * View mode for Parts Inventory ("List" → list, anything else → tile).
   * Stored separately from viewFilter so DashboardScreen can defer clearing
   * until PartsInventory has mounted and consumed it.
   */
  partsInventoryViewFilter?: string;
  /**
   * 1-based page number to jump to in the Parts Inventory list.
   * Sent when Nova AI returns Path["Parts"] + filters.page.
   */
  partsInventoryPageFilter?: number;
  /**
   * Status label to apply in the Parts Inventory filter dropdown.
   * Matched case-insensitively against allStatuses[].label
   * (e.g. "Reserved" → the option whose label === 'Reserved').
   */
  partsInventoryStatusFilter?: string;
  /**
   * Set when Nova AI answered a message question ("how many unread messages do
   * I have", "read me the latest message") and the Parts Messages screen is
   * where that message is shown in full. The value is the message filter to
   * land on ("Unread" today, which is also MessagesView's own default).
   * PartsScreen consumes it and clears the navigation.
   */
  partsMessagesFilter?: string;
  /**
   * Search category key to pre-select in the repairs search-category dropdown.
   * Matches against repairSortOptions[].value (e.g. "license", "asset number").
   */
  searchTextFilter?: string;
  /**
   * Search value to pre-fill in the search input when searchTextFilter is set
   * (e.g. "ABC1234" when searchTextFilter is "license").
   */
  searchValueFilter?: string;
}

/**
 * The draft Nova AI staged on one task — "mark task 1 as Completed", "add a
 * comment to task 1: …", "remove the comment from task 1". The whole draft
 * travels each time, so a comment staged after a resolution shows beside it.
 * `comment` is "" when Nova cleared it, and absent when Nova did not touch it.
 */
export interface NovaAITaskEditStage {
  stepNumber: number;
  comment?: string;
  resultId?: number;
  resultName?: string;
}

interface NovaAIState {
  /**
   * Whether the Nova AI chat icon should be shown on the technician dashboard.
   * Sourced from the `isNovaAIEnabled` field in the validate-agency-name API
   * response and dispatched during bootstrap / agency validation.
   */
  isNovaAIEnabled: boolean;
  repairNavigation?: NovaAIRepairNavigation;
  /**
   * Incremented each time Nova AI wants to reload the current screen in place
   * (API response contains Reload: true). DashboardScreen watches this counter
   * and triggers fetchData() when it changes.
   */
  homeReloadSignal: number;
  /**
   * Bumped when Nova AI committed a change to a repair while the technician is
   * on the Repairs list (no repair open). WorkOrdersScreen refetches its list
   * when this changes — only if the repair is one it is showing, when known.
   */
  repairsReloadSignal: number;
  repairsReloadRepairId?: string;
  /**
   * Bumped when Nova AI discarded the unsaved edit on a task card ("discard
   * the change to task 1"). TasksView closes the matching card the way its
   * Cancel button does, so what was tapped or typed on screen is dropped too.
   */
  taskEditDiscardSignal: number;
  /** The task the discard named; undefined means whichever card is open. */
  taskEditDiscardStep?: number;
  /**
   * Bumped when Nova AI staged an edit on a task ("mark task 1 as Completed",
   * "add a comment to task 1"). TasksView opens that card with the draft in
   * it, unsaved — as if the technician had tapped and typed — so Save (or
   * "save task 1") commits exactly what the chat said.
   */
  taskEditStageSignal: number;
  taskEditStage?: NovaAITaskEditStage;
  /**
   * Bumped when Nova AI read one note aloud ("view note", "open note 2", the
   * answer to its "which note?" question). NotesView opens the View popup on
   * that note, the way the row's own View button does.
   */
  noteOpenSignal: number;
  /** The note Nova read out, by id. */
  noteOpenId?: string;
  /**
   * Bumped when Nova AI submitted a part request ("request 2 units of part
   * oil", "add my own request"). PartsView opens the Parts Request History
   * popup, where the request that was just made is listed.
   */
  partsHistorySignal: number;
  /**
   * Bumped when Nova AI searched the parts for this repair ("search for part
   * oil"). PartsView puts the term in its own search box, so the rows appear
   * where the technician requests them from, not only in the chat table.
   */
  partsSearchSignal: number;
  /** The term Nova searched for. */
  partsSearchTerm?: string;
  /**
   * The Configured Part List panel, driven by Nova in place. Three things a
   * technician does there by voice: set a row's quantity, tick a row, and
   * submit what is ticked. Nova knows each row's partID because it reads the
   * same list from the API, but the panel owns the selection and the submit —
   * including the Confirm Parts Request screen — so these are signals to it
   * rather than work Nova does itself.
   */
  configuredQtySignal: number;
  /** Whose box to fill, by partID, and with what. */
  configuredQtyPartId?: string;
  configuredQtyValue?: string;
  /** Bumped to tick one row's checkbox; the row, by partID. */
  configuredSelectSignal: number;
  configuredSelectPartId?: string;
  /**
   * Bumped once Nova AI has submitted the request itself, to the same endpoint
   * the panel's Confirm Parts Request screen posts to. The panel clears its
   * ticks and shows the history; it does not submit anything.
   */
  configuredSubmittedSignal: number;
  /**
   * Bumped when Nova takes the technician somewhere while a create flow (New
   * Repair / New Work Order) is open: MainLayoutScreen leaves it the way a
   * left-menu tap does. `flowExitTab` is the tab to go to once a protected
   * step's "leave this flow?" is confirmed; null when the flow simply closes
   * and Nova's own navigation carries on.
   */
  flowExitSignal: number;
  flowExitTab?: string | null;
}

const initialState: NovaAIState = {
  isNovaAIEnabled: false,
  homeReloadSignal: 0,
  repairsReloadSignal: 0,
  taskEditDiscardSignal: 0,
  taskEditStageSignal: 0,
  noteOpenSignal: 0,
  partsHistorySignal: 0,
  partsSearchSignal: 0,
  configuredQtySignal: 0,
  configuredSelectSignal: 0,
  configuredSubmittedSignal: 0,
  flowExitSignal: 0,
  flowExitTab: null,
};

const novaAISlice = createSlice({
  name: 'novaAI',
  initialState,
  reducers: {
    /**
     * Sets whether the Nova AI chat icon is enabled for this agency.
     * Dispatched during bootstrap / agency validation from the
     * `isNovaAIEnabled` field in the validate-agency-name API response.
     */
    setNovaAIEnabled(state, action: PayloadAction<boolean>) {
      state.isNovaAIEnabled = action.payload;
    },
    /** Stores the repair tab + scope filter to apply when the Repairs screen mounts/focuses. */
    setNovaAIRepairNavigation(
      state,
      action: PayloadAction<NovaAIRepairNavigation>,
    ) {
      state.repairNavigation = action.payload;
    },
    /** Clears the navigation intent once WorkOrdersScreen has consumed it. */
    clearNovaAIRepairNavigation(state) {
      state.repairNavigation = undefined;
    },
    /**
     * Clears only searchTextFilter and searchValueFilter, leaving all other
     * navigation fields (statusFilter, scopeFilter, etc.) intact.
     * Dispatched by RepairsSection after it consumes the search intent so that
     * sibling effects (WorkOrdersScreen / DashboardScreen) can still apply the
     * remaining filters without racing against the search clear.
     */
    /**
     * Drops the priority and status once the repairs list has applied them.
     *
     * clearDashboardNavigation keeps both alive across the render where Home's
     * sections mount, because the section that reads them mounts late. Nothing
     * then let go of them: they sat in the store, and the next repairs list to
     * mount — on Home or on the Repairs screen — read them and applied URGENT
     * again, to a technician who had just navigated away and back. Only these
     * two, not the whole navigation, so anything else still waiting to be read
     * (a search, a view) is left for its own reader. Reported by Kasi,
     * 30 Sep 2026.
     */
    consumeNovaAIRepairFilters(state) {
      if (!state.repairNavigation) return;
      const {sortFilter: _s, statusFilter: _t, ...rest} = state.repairNavigation;
      state.repairNavigation = Object.keys(rest).length > 0 ? rest : undefined;
    },
    clearSearchNavigation(state) {
      if (!state.repairNavigation) return;
      delete state.repairNavigation.searchTextFilter;
      delete state.repairNavigation.searchValueFilter;
      const hasRemaining = Object.values(state.repairNavigation).some(
        v => v !== undefined,
      );
      if (!hasRemaining) {
        state.repairNavigation = undefined;
      }
    },
    /**
     * Clears the dashboard-level navigation fields (statusFilter, scopeFilter,
     * dashboardTabFilter, viewFilter, sortFilter) but intentionally leaves
     * partsInventoryTabFilter and partsInventoryViewFilter intact so that
     * PartsInventory can read them after it mounts (it clears them via
     * clearNovaAIRepairNavigation).
     */
    clearDashboardNavigation(state) {
      if (!state.repairNavigation) return;
      const {
        partsInventoryTabFilter,
        partsInventoryViewFilter,
        partsMessagesFilter,
        viewFilter,
        searchTextFilter,
        searchValueFilter,
        sortFilter,
        statusFilter,
        partsInventoryStatusFilter,
      } = state.repairNavigation;
      // Preserve fields that child components (PartsInventory, RepairsSection)
      // still need to read after they mount in the next render cycle.
      const preserved: NovaAIRepairNavigation = {};
      if (partsInventoryTabFilter) preserved.partsInventoryTabFilter = partsInventoryTabFilter;
      if (partsInventoryViewFilter) preserved.partsInventoryViewFilter = partsInventoryViewFilter;
      // Belongs to PartsScreen, which mounts after the tab switch — the
      // dashboard must not clear it on its way out.
      if (partsMessagesFilter) preserved.partsMessagesFilter = partsMessagesFilter;
      // viewFilter belongs to the dashboard's repairs section, which reads it
      // in its own effect. Dropping it here cleared the value before that ran,
      // so a view switch on the dashboard did nothing at all.
      if (viewFilter) preserved.viewFilter = viewFilter;
      if (searchTextFilter) preserved.searchTextFilter = searchTextFilter;
      if (searchValueFilter) preserved.searchValueFilter = searchValueFilter;
      // The filters themselves, for the reason viewFilter is kept above: the
      // section they belong to mounts a render after this runs. Traced 30 Sep
      // 2026 — sortFilter "UR" reached the dashboard effect, then this kept {}
      // and the Repairs section mounted to find nothing.
      if (sortFilter) preserved.sortFilter = sortFilter;
      if (statusFilter) preserved.statusFilter = statusFilter;
      if (partsInventoryStatusFilter) {
        preserved.partsInventoryStatusFilter = partsInventoryStatusFilter;
      }
      const hasPreserved = Object.keys(preserved).length > 0;
      state.repairNavigation = hasPreserved ? preserved : undefined;
    },
    /**
     * Increments homeReloadSignal to tell DashboardScreen to re-fetch data.
     * Dispatched when the Nova AI API returns Reload: true on the Home screen.
     */
    triggerHomeReload(state) {
      state.homeReloadSignal += 1;
    },
    triggerPartsSearch(state, action: PayloadAction<string>) {
      state.partsSearchSignal += 1;
      state.partsSearchTerm = action.payload;
    },
    triggerPartsHistory(state) {
      state.partsHistorySignal += 1;
    },
    triggerConfiguredQty(
      state,
      action: PayloadAction<{partId: string; qty: string}>,
    ) {
      state.configuredQtySignal += 1;
      state.configuredQtyPartId = action.payload.partId;
      state.configuredQtyValue = action.payload.qty;
    },
    triggerConfiguredSelect(state, action: PayloadAction<string>) {
      state.configuredSelectSignal += 1;
      state.configuredSelectPartId = action.payload;
    },
    triggerConfiguredSubmitted(state) {
      state.configuredSubmittedSignal += 1;
    },
    requestFlowExit(state, action: PayloadAction<string | null>) {
      state.flowExitSignal += 1;
      state.flowExitTab = action.payload;
    },
    triggerNoteOpen(state, action: PayloadAction<string>) {
      state.noteOpenSignal += 1;
      state.noteOpenId = action.payload;
    },
    triggerTaskEditDiscard(state, action: PayloadAction<number | undefined>) {
      state.taskEditDiscardSignal += 1;
      state.taskEditDiscardStep = action.payload;
    },
    triggerTaskEditStage(state, action: PayloadAction<NovaAITaskEditStage>) {
      state.taskEditStageSignal += 1;
      state.taskEditStage = action.payload;
    },
    /**
     * Tell the Repairs list that a repair changed. `payload` is the repair id
     * when Nova knows it, so the list can skip the refetch if that repair is
     * not on screen; undefined means "something changed, refetch".
     */
    triggerRepairsReload(state, action: PayloadAction<string | undefined>) {
      state.repairsReloadSignal += 1;
      state.repairsReloadRepairId = action.payload || undefined;
    },
  },
});

export const {
  setNovaAIEnabled,
  setNovaAIRepairNavigation,
  clearNovaAIRepairNavigation,
  clearSearchNavigation,
  clearDashboardNavigation,
  consumeNovaAIRepairFilters,
  triggerHomeReload,
  triggerRepairsReload,
  triggerTaskEditDiscard,
  triggerTaskEditStage,
  triggerNoteOpen,
  triggerPartsHistory,
  triggerPartsSearch,
  triggerConfiguredQty,
  triggerConfiguredSelect,
  triggerConfiguredSubmitted,
  requestFlowExit,
} = novaAISlice.actions;
export default novaAISlice.reducer;